import { computed } from 'vue';
import { useAnalyticsStore } from '@/stores/analytics';
import { useUserStore } from '@/stores/user';

type CategoryWidgets = {
  category: string;
  id: string;
  enabled?: boolean;
  isPremium?: boolean
  widgets: WidgetPreview[];
};

export type WidgetPreview = {
  id: number;
  name: string;
  description: string;
  isBenchmarking?: boolean;
  isPremium?: boolean;
  default_width?: number;
  default_height?: number;
  previewText?: string;
  previewImage?: string;
};

export function useWidgets() {
  const userStore = useUserStore();
  const analyticsStore = useAnalyticsStore();

  const widgetsByCategory = computed<CategoryWidgets[]>(() => [
    {
      category: 'Performance',
      id: 'performance',
      enabled: true,
      widgets: [
        {
          id: 3,
          name: 'Unique Visitors',
          description:
            'Unique Visitors: Total distinct detected visitors. Conversion Rate: Percentage of visitors that became engaged.',
          default_width: 2,
          default_height: 2,
        },
        {
          id: 1,
          name: 'Engaged Visit Dwell Time',
          description:
            'Average time spent within zones per individual visit. Separate visits by the same visitor are treated individually.',
        },
        {
          id: 2,
          name: 'Engaged Visitor Dwell Time',
          description:
            'Average total time spent within zones per visitor. Separate visits by the same visitor are summed.',
        },
        {
          id: 4,
          name: 'Average Engaged Visits',
          description: 'Average number of times seen within zones per visitor.',
          default_width: 1,
          default_height: 1,
        },
        {
          id: 5,
          name: 'Average Repeats',
          description:
            'Average number of visits beyond the first by a visitor. Three visits in the same day by a visitor will count as two repeated visits beyond the first.',
          default_width: 1,
          default_height: 1,
        },
      ],
    },
    {
      category: 'Activations',
      id: 'activations',
      enabled: true,
      widgets: [
        {
          id: 6,
          name: 'Zones Visited',
          description: 'The division of total engaged visits across all zones.',
          default_width: 2,
          default_height: 2,
        },
        {
          id: 12,
          name: 'Days Visited',
          description: 'Number of days a visitor was seen on.',
        },
      ],
    },
    {
      category: 'Engagement',
      id: 'engagement',
      enabled: true,
      widgets: [
        {
          id: 11,
          name: 'Engaged Visit Distributed Dwell',
          description: 'Percentage of visits that lasted each duration in minutes.',
        },
        {
          id: 8,
          name: 'Engaged Visits',
          description: 'Detections of engaged visitors.',
        },
        {
          id: 10,
          name: 'Repeat Visitors',
          description:
            'Total visitors detected multiple times either within the same day or between days.',
        },
        {
          id: 9,
          name: 'Repeat Visits',
          description: 'Total visits that are not the first visit made by each visitor.',
        },
      ],
    },
    ...(userStore.hasFeature('basic_benchmarking_widgets')
      ? [
          {
            category: 'Benchmarking',
            id: 'benchmarking',
            enabled: true,
            widgets: [
              {
                id: 15,
                name: 'Zone Engaged Repeat Visits',
                description: 'Engaged visits beyond the first engaged visit, per zone.',
                isBenchmarking: true,
              },
              {
                id: 13,
                name: 'Zone Conversion Rate',
                description:
                  'The percentage of Opportunity To See (OTS) that became engaged, per zone.',
                isBenchmarking: true,
              },
              {
                id: 14,
                name: 'Zone Engaged Dwell Time',
                description: 'Average visit time for engaged visits, per zone.',
                isBenchmarking: true,
              },
            ],
          },
        ]
      : []),
    ...(analyticsStore.eventHasFeature('premium_metrics')
      ? [
          {
            category: 'Premium',
            id: 'premium',
            enabled: true,
            isPremium: true,
            widgets: [
              {
                id: 17,
                name: 'Cumulative Arrival',
                description: 'Percentage of visitors arriving on site over time.',
                isPremium: true,
                previewText: 'Reveal visitor arrival patterns over time to anticipate peak periods and improve capacity planning',
                previewImage: 'cumulative-arrival.png',
              },
              {
                id: 18,
                name: 'Arrivals by Day',
                description: 'Count of when visitors arrived on site.',
                default_width: 2,
                default_height: 2,
                isPremium: true,
                previewText: 'Count of when visitors arrived on site.',
                previewImage: 'arrivals-by-day.png',
              },
              {
                id: 20,
                name: 'Most Common Path',
                description: 'Most popular zone visit sequences across the event.',
                default_width: 2,
                default_height: 2,
                isPremium: true,
                previewText: 'Find out the top most commonly travelled by the visitors at an event',
              },
              {
                id: 25,
                name: 'Zone-to-Zone Crossover',
                description:
                  'Visitor movement between zones, as a percentage of visitors to that zone. Ordered Overlap only counts visitors that were later seen at the second zone.',
                default_width: 2,
                default_height: 2,
                isPremium: true,
                previewText: 'Find out the percentage of visitors who engaged at one zone and also engaged at another',
              },
              {
                id: 26,
                name: 'Total Engaged Dwell Time by Zone',
                description: 'Distribution of engaged time spent by visitors across zones.',
                default_width: 1,
                default_height: 2,
                isPremium: true,
                previewText: 'Distribution of engaged time spent by visitors across zones.',
                previewImage: 'dwell-by-zone.png',
              },
              {
                id: 27,
                name: 'Zone Count Visited',
                description: 'The number of zones visited by each visitor.',
                default_width: 1,
                default_height: 2,
                isPremium: true,
              },
              {
                id: 28,
                name: 'First Zone Visited',
                description: 'The first zone visited by each visitor.',
                default_width: 1,
                default_height: 2,
                isPremium: true,
              },
            ],
          },
        ]
      : []),
  ]);

  return {
    widgetPreview: {} as WidgetPreview,
    widgetsByCategory,
  };
}
